import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconCheck,
  IconChevronRight,
  IconClock,
  IconFileTypeCsv,
  IconFileTypeXls,
  IconInfoCircle,
  IconPackageImport,
  IconPlus,
  IconDownload,
  IconTags,
} from '@tabler/icons-react';

import {
  emptyFoundationsSignals,
  foundationsProgressPercent,
  isFoundationStepComplete,
  isFoundationsComplete,
  SETUP_ENSURED_ROLE_NAMES,
  SETUP_ROLE_SUGGESTION_NAMES,
  SETUP_SEEDED_ROLE_NAMES,
  CANONICAL_PRODUCT_TAG_GROUPS,
  type FoundationsSignals,
  type FoundationsStepId,
} from '@wapharma/domain';
import {
  Avatar,
  Button,
  ConfirmModalFooter,
  IconButton,
  InlineError,
  Modal,
  overlayPresenceProps,
  ProximityScope,
  Text,
  Tooltip,
  WP_ICON_NAV,
  cx,
} from '@wapharma/design-system';
import { unwrapData } from '@wapharma/shared';
import { useAuth } from '../contexts/AuthContext';
import { StepTravel } from './StepTravel';
import { SetupWizardPathArt } from './SetupWizardPathArt';
import {
  capabilityPackDescriptionFr,
  capabilityPackLabelFr,
  userRoleDescriptionFr,
  userRoleLabelFr,
  userRolePacks,
} from '../constants/userRoleDisplay';
import { RoleAccessCoverageCell } from './RoleAccessCoverageCell';
import { apiGet, apiPost } from '../api';
import { apiGetListSimple } from '../apiList';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { isSetupDismissed, setSetupDismissed } from '../lib/setupPrefs';
import { rolePersonaDataUri } from '../lib/rolePersonaAvatar';
import {
  beginAwaitCustomRoleCreate,
  beginAwaitLocationCreate,
  beginAwaitMarkeCreate,
  beginAwaitMarkeImport,
  beginAwaitProductTypeCreate,
  beginAwaitRolesBulkImport,
  beginAwaitStaffUserCreate,
  beginAwaitStaffUsersImport,
  beginAwaitStructureCreate,
  beginAwaitTagFamilyCreate,
  beginAwaitTagsBulkImport,
  getSetupWizardUiState,
  setSetupWizardUiState,
  subscribeCustomRoleCreate,
  subscribeLocationCreate,
  subscribeMarkeCreate,
  subscribeMarkeImport,
  subscribeOpenSetup,
  subscribeProductTypeCreate,
  subscribeRolesBulkImport,
  subscribeSetupWizardUiState,
  subscribeStaffUserCreate,
  subscribeStaffUsersBulkImport,
  subscribeStructureCreate,
  subscribeTagFamilyCreate,
  subscribeTagsBulkImport,
} from '../lib/setupShell';
import { openLaunchWizard } from '../lib/launchShell';
import { MarkeLogoThumb } from './MarkeLogoField';
import { StockAlertsSettingsSection, type StockAlertsSettingsHandle } from './StockAlertsSettingsSection';
import { downloadRolesImportTemplate } from './useRolesBulkImport';
import {
  downloadTaxonomyFacetImportTemplate,
} from './useTaxonomyFacetBulkImport';
import { downloadUsersImportTemplate } from './useUsersBulkImport';
import { UserRolePill } from './UserRolePill';
import { formatUserFullName, type UserRow } from '../pages/users/usersPageHelpers';
import { ADMIN_USERS } from '../constants/adminNavLabels';
import type { CatRow, ProductTypeRow } from '../pages/taxonomy/taxonomyShared';
import { ProductTypeTaxonomyGraph } from './ProductTypeTaxonomyGraph';
import dockStyles from './OnboardingDock.module.css';
import styles from './SetupWizard.module.css';

const SETUP_HEIGHT_VAR = '--wp-setup-dock-height';
const SETUP_PAD_VAR = '--wp-setup-dock-pad';

type FoundationsReadinessPayload = {
  foundations: FoundationsSignals;
  foundationsComplete: boolean;
  foundationsProgressPercent: number;
};

function publishSetupMetrics(sheet: HTMLElement): void {
  const fullH = Math.ceil(sheet.getBoundingClientRect().height);
  const strip = sheet.querySelector('[data-wp-dock-strip]');
  const stripH = strip
    ? Math.ceil(strip.getBoundingClientRect().height)
    : fullH;
  document.documentElement.style.setProperty(SETUP_HEIGHT_VAR, `${fullH}px`);
  document.documentElement.style.setProperty(SETUP_PAD_VAR, `${stripH}px`);
}

/** Deterministic DiceBear Notionists face per role. */
function RoleTagFace({ roleName }: { roleName: string }) {
  return (
    <img
      className={styles.roleTagFace}
      src={rolePersonaDataUri(roleName)}
      alt=""
      width={28}
      height={28}
      decoding="async"
    />
  );
}

function RoleCapabilityTip({
  roleName,
  packs: packsProp,
}: {
  roleName: string;
  packs?: readonly string[];
}) {
  const packs = userRolePacks(roleName, packsProp);
  return (
    <div className={styles.roleTipCard} role="presentation">
      <Text size="sm" fw={600} className={styles.roleTipTitle}>
        {userRoleLabelFr(roleName)}
      </Text>
      <Text size="xs" c="dimmed" className={styles.roleTipDesc}>
        {userRoleDescriptionFr(roleName, packs)}
      </Text>
      <RoleAccessCoverageCell packs={packs} />
      {packs.length > 0 ? (
        <ul className={styles.roleTipPacks}>
          {packs.map((pack) => (
            <li key={pack}>
              <strong>{capabilityPackLabelFr(pack)} :</strong> {capabilityPackDescriptionFr(pack)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Scrollable role tag grid — mask fade via `.wp-scroll-fade` on scroller. */
function RoleTagGridShell({
  className,
  'aria-label': ariaLabel,
  children,
}: {
  className?: string;
  'aria-label'?: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.roleTagGridShell}>
      <div
        className={['wp-scroll-fade', className].filter(Boolean).join(' ')}
        role="list"
        aria-label={ariaLabel}
        data-wp-mist-scroll=""
      >
        {children}
      </div>
    </div>
  );
}

/** Team role rows — same mist scroll + cross-fade mask. */
function TeamRoleListShell({
  className,
  'aria-label': ariaLabel,
  children,
}: {
  className?: string;
  'aria-label'?: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.teamRoleListShell}>
      <div
        className={['wp-scroll-fade', className].filter(Boolean).join(' ')}
        aria-label={ariaLabel}
        data-wp-mist-scroll=""
      >
        {children}
      </div>
    </div>
  );
}

const STEPS: { id: FoundationsStepId; label: string }[] = [
  { id: 'roles', label: 'Rôles' },
  { id: 'locations', label: 'Emplacements' },
  { id: 'team', label: 'Équipe' },
  { id: 'markes', label: 'Marques' },
  { id: 'structure', label: 'Structure' },
  { id: 'tags', label: 'Tags' },
  { id: 'pref_low_stock', label: 'Stock bas' },
  { id: 'pref_near_expiry', label: 'Péremption' },
];

/** Resume setup on the step that launched a companion wizard (never hardcode indices). */
function setupStepIndex(id: FoundationsStepId): number {
  const i = STEPS.findIndex((s) => s.id === id);
  return i >= 0 ? i : 0;
}

/** Suggested locations — same names as demo seed warehouse. */
const LOCATION_SEED_MODELS = [
  {
    type: 'WAREHOUSE' as const,
    name: 'Entrepôt principal',
    label: 'Entrepôt',
  },
  {
    type: 'SHOWROOM_OUTLET' as const,
    name: 'Point de vente',
    label: 'Point de vente',
  },
];

function foundationsFromPayload(
  raw: FoundationsSignals | null | undefined,
): FoundationsSignals {
  if (!raw) return emptyFoundationsSignals();
  return raw;
}

export function SetupWizard() {
  const { user, mustChangePassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = useId();
  const sheetRef = useRef<HTMLElement | null>(null);
  const stockBasRef = useRef<StockAlertsSettingsHandle>(null);
  const peremptionRef = useRef<StockAlertsSettingsHandle>(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [collapseInstant, setCollapseInstant] = useState(false);
  const [awaitingRoleCreate, setAwaitingRoleCreate] = useState(false);
  const [awaitingRoleImport, setAwaitingRoleImport] = useState(false);
  const [awaitingStaffUser, setAwaitingStaffUser] = useState(false);
  const [awaitingStaffUsersImport, setAwaitingStaffUsersImport] = useState(false);
  const [awaitingMarkeCreate, setAwaitingMarkeCreate] = useState(false);
  const [awaitingMarkeImport, setAwaitingMarkeImport] = useState(false);
  const [awaitingProductTypeCreate, setAwaitingProductTypeCreate] = useState(false);
  const [awaitingStructureCreate, setAwaitingStructureCreate] = useState(false);
  const [dismissConfirmOpen, setDismissConfirmOpen] = useState(false);
  const [rolesImportDone, setRolesImportDone] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [autoTried, setAutoTried] = useState(false);
  /** After deep-link: reopen sheet (not dismissed — only paused for navigation). */
  const [reopenAfterNav, setReopenAfterNav] = useState(false);
  const [customRoles, setCustomRoles] = useState<
    Array<{ id: string; name: string; packs: string[] }>
  >([]);
  const [suggestedRolesPresent, setSuggestedRolesPresent] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [roleSuggestBusy, setRoleSuggestBusy] = useState(false);
  const [roleSuggestErr, setRoleSuggestErr] = useState<string | null>(null);
  const [setupMarkes, setSetupMarkes] = useState<
    Array<{ id: string; name: string; logoUrl: string | null }>
  >([]);
  const [setupProductTypes, setSetupProductTypes] = useState<ProductTypeRow[]>([]);
  const [setupCategories, setSetupCategories] = useState<CatRow[]>([]);
  const [setupSelectedTypeCode, setSetupSelectedTypeCode] = useState('');
  const [taxonomySeedModels, setTaxonomySeedModels] = useState<{
    productTypes: Array<{ code: string; label: string; present: boolean }>;
    rootCategories: Array<{ slug: string; name: string; present: boolean }>;
    complete: boolean;
    preview: {
      productTypes: ProductTypeRow[];
      categories: CatRow[];
    };
  } | null>(null);
  const [seedPreviewTypeCode, setSeedPreviewTypeCode] = useState('');
  const [taxonomySeedBusy, setTaxonomySeedBusy] = useState(false);
  const [taxonomySeedErr, setTaxonomySeedErr] = useState<string | null>(null);

  const [setupTagFamilies, setSetupTagFamilies] = useState<
    Array<{ id: string; code: string; name: string; tagCount: number }>
  >([]);
  const [tagSeedBusy, setTagSeedBusy] = useState(false);
  const [tagSeedErr, setTagSeedErr] = useState<string | null>(null);
  const [awaitingTagFamilyCreate, setAwaitingTagFamilyCreate] = useState(false);
  const [awaitingTagsImport, setAwaitingTagsImport] = useState(false);
  const [setupLocations, setSetupLocations] = useState<
    Array<{ id: string; name: string; type: string | null }>
  >([]);
  const [locationSeedBusy, setLocationSeedBusy] = useState(false);
  const [locationSeedErr, setLocationSeedErr] = useState<string | null>(null);
  const [awaitingLocationCreate, setAwaitingLocationCreate] = useState(false);
  const [readiness, setReadiness] = useState<FoundationsReadinessPayload | null>(null);

  const refreshReadiness = useCallback(async () => {
    const token = getToken();
    if (!token) return null;
    try {
      const raw = await apiGet<{ data: FoundationsReadinessPayload } | FoundationsReadinessPayload>(
        '/reports/foundations-readiness',
        token,
      );
      const payload = unwrapData(raw) as FoundationsReadinessPayload;
      setReadiness(payload);
      return payload;
    } catch {
      return null;
    }
  }, []);

  const foundations = foundationsFromPayload(readiness?.foundations ?? null);
  const complete = readiness?.foundationsComplete ?? isFoundationsComplete(foundations);
  const pct = readiness?.foundationsProgressPercent ?? foundationsProgressPercent(foundations);

  const loadPresentRoles = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{
        id: string;
        name: string;
        seeded?: boolean;
        packs?: string[];
      }>('/admin/roles', token);
      const custom: Array<{ id: string; name: string; packs: string[] }> = [];
      const suggested = new Set<string>();
      for (const r of rows) {
        if ((SETUP_ROLE_SUGGESTION_NAMES as readonly string[]).includes(r.name)) {
          suggested.add(r.name);
        }
        if (SETUP_SEEDED_ROLE_NAMES.includes(r.name)) continue;
        custom.push({ id: r.id, name: r.name, packs: r.packs ?? [] });
      }
      setSuggestedRolesPresent(suggested);
      setCustomRoles(custom);
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  /** Platform bootstrap: SUPER_ADMIN + SHOWROOM_STAFF (Caissier) + STOREFRONT only. */
  const ensureBootstrapRoles = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      await apiPost('/admin/roles/ensure-system', {}, token);
      await refreshReadiness();
      await loadPresentRoles();
    } catch {
      /* seed/migration may already have them */
    }
  }, [refreshReadiness, loadPresentRoles]);

  const roleSuggestComplete = SETUP_ROLE_SUGGESTION_NAMES.every((n) =>
    suggestedRolesPresent.has(n),
  );

  const installSuggestedRoleModels = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setRoleSuggestErr('Session requise.');
      return;
    }
    setRoleSuggestBusy(true);
    setRoleSuggestErr(null);
    try {
      await apiPost('/admin/roles/ensure-suggested', {}, token);
      await refreshReadiness();
      await loadPresentRoles();
    } catch (e: unknown) {
      setRoleSuggestErr(getDisplayError(e));
    } finally {
      setRoleSuggestBusy(false);
    }
  }, [refreshReadiness, loadPresentRoles]);

  const loadSetupMarkes = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{
        id: string;
        name: string;
        logoUrl?: string | null;
      }>('/catalog/admin/markes', token);
      setSetupMarkes(
        rows.map((r) => ({
          id: r.id,
          name: r.name,
          logoUrl: r.logoUrl ?? null,
        })),
      );
    } catch {
      /* count from readiness still drives Continuer */
    }
  }, []);

  const loadSetupProductTypes = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<ProductTypeRow>('/catalog/admin/product-types', token);
      setSetupProductTypes(
        rows.map((r) => ({
          ...r,
          roots: Array.isArray(r.roots) ? r.roots : [],
        })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadSetupCategories = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<CatRow>('/catalog/admin/categories', token);
      setSetupCategories(
        rows.map((r) => ({
          id: r.id,
          name: r.name,
          slug: r.slug,
          parentId: r.parentId ?? null,
          imageUrl: r.imageUrl ?? null,
        })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadSetupLocations = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{ id: string; name: string; type?: string | null }>(
        '/inventory/locations',
        token,
      );
      setSetupLocations(
        rows.map((r) => ({ id: r.id, name: r.name, type: r.type ?? null })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadTaxonomySeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      type SeedModelsBody = {
        productTypes: Array<{ code: string; label: string; present: boolean }>;
        rootCategories: Array<{ slug: string; name: string; present: boolean }>;
        complete: boolean;
        preview?: {
          productTypes: ProductTypeRow[];
          categories: CatRow[];
        };
      };
      const raw = await apiGet<{ data: SeedModelsBody } | SeedModelsBody>(
        '/catalog/admin/taxonomy/seed-models',
        token,
      );
      const body = unwrapData(raw) as SeedModelsBody;
      setTaxonomySeedModels({
        productTypes: body.productTypes ?? [],
        rootCategories: body.rootCategories ?? [],
        complete: Boolean(body.complete),
        preview: {
          productTypes: (body.preview?.productTypes ?? []).map((r) => ({
            ...r,
            imageUrl: r.imageUrl ?? null,
            roots: Array.isArray(r.roots) ? r.roots : [],
          })),
          categories: (body.preview?.categories ?? []).map((r) => ({
            id: r.id,
            name: r.name,
            slug: r.slug,
            parentId: r.parentId ?? null,
            imageUrl: r.imageUrl ?? null,
          })),
        },
      });
    } catch {
      /* optional — create still works */
    }
  }, []);

  const downloadFacetTemplate = useCallback(
    (facet: 'markes' | 'product-types' | 'categories' | 'tags', format: 'xlsx' | 'csv') => {
      const token = getToken();
      if (!token) return;
      void downloadTaxonomyFacetImportTemplate(token, facet, format);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    void ensureBootstrapRoles();
  }, [open, ensureBootstrapRoles]);

  useEffect(() => {
    if (!open) return;
    void loadPresentRoles();
  }, [open, loadPresentRoles, foundations.seededRoleCount, foundations.customRoleCount]);

  useEffect(() => {
    if (!open) return;
    void loadSetupMarkes();
  }, [open, loadSetupMarkes, foundations.markeCount]);

  useEffect(() => {
    if (!open) return;
    void loadSetupProductTypes();
  }, [open, loadSetupProductTypes, foundations.productTypeCount]);

  useEffect(() => {
    if (!open) return;
    void loadSetupCategories();
  }, [open, loadSetupCategories, foundations.categoryCount]);

  const loadSetupTagFamilies = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{
        id: string;
        code: string;
        name: string;
        tags?: Array<{ id: string }>;
      }>('/catalog/admin/tags/families', token);
      setSetupTagFamilies(
        rows.map((r) => ({
          id: r.id,
          code: r.code,
          name: r.name,
          tagCount: Array.isArray(r.tags) ? r.tags.length : 0,
        })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadSetupTagFamilies();
  }, [open, loadSetupTagFamilies, foundations.tagFamilyWithValuesCount]);


  useEffect(() => {
    if (!open) return;
    void loadTaxonomySeedModels();
  }, [
    open,
    loadTaxonomySeedModels,
    foundations.productTypeCount,
    foundations.categoryCount,
  ]);

  useEffect(() => {
    if (!open) return;
    void loadSetupLocations();
  }, [open, loadSetupLocations, foundations.locationCount]);

  const locationSeedModels = useMemo(() => {
    const byType = new Set(
      setupLocations.map((l) => l.type).filter((t): t is string => Boolean(t)),
    );
    return LOCATION_SEED_MODELS.map((m) => ({
      ...m,
      present: byType.has(m.type),
    }));
  }, [setupLocations]);

  const locationSeedComplete = locationSeedModels.every((m) => m.present);

  const setupSortedProductTypes = useMemo(() => {
    return [...setupProductTypes].sort((a, b) => {
      const o = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      if (o !== 0) return o;
      return (a.label ?? '').localeCompare(b.label ?? '', 'fr');
    });
  }, [setupProductTypes]);

  const hasSetupTaxonomyTree =
    setupSortedProductTypes.length > 0 || setupCategories.length > 0;

  useEffect(() => {
    if (setupSortedProductTypes.length === 0) {
      if (setupSelectedTypeCode) setSetupSelectedTypeCode('');
      return;
    }
    if (!setupSortedProductTypes.some((t) => t.code === setupSelectedTypeCode)) {
      setSetupSelectedTypeCode(setupSortedProductTypes[0]!.code);
    }
  }, [setupSortedProductTypes, setupSelectedTypeCode]);

  const reloadSetupTaxonomy = useCallback(async () => {
    await Promise.all([loadSetupProductTypes(), loadSetupCategories(), loadTaxonomySeedModels()]);
  }, [loadSetupProductTypes, loadSetupCategories, loadTaxonomySeedModels]);

  useEffect(() => {
    const types = taxonomySeedModels?.preview.productTypes ?? [];
    if (types.length === 0) {
      if (seedPreviewTypeCode) setSeedPreviewTypeCode('');
      return;
    }
    if (!types.some((t) => t.code === seedPreviewTypeCode)) {
      setSeedPreviewTypeCode(types[0]!.code);
    }
  }, [taxonomySeedModels, seedPreviewTypeCode]);

  const installTaxonomySeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setTaxonomySeedErr('Session requise.');
      return;
    }
    setTaxonomySeedBusy(true);
    setTaxonomySeedErr(null);
    try {
      await apiPost('/catalog/admin/taxonomy/ensure-seed', {}, token);
      await loadTaxonomySeedModels();
      await refreshReadiness();
      await Promise.all([loadSetupProductTypes(), loadSetupCategories()]);
    } catch (e: unknown) {
      setTaxonomySeedErr(getDisplayError(e));
    } finally {
      setTaxonomySeedBusy(false);
    }
  }, [refreshReadiness, loadSetupProductTypes, loadSetupCategories, loadTaxonomySeedModels]);

  const installLocationSeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLocationSeedErr('Session requise.');
      return;
    }
    setLocationSeedBusy(true);
    setLocationSeedErr(null);
    try {
      const existing = await apiGetListSimple<{ id: string; type?: string | null }>(
        '/inventory/locations',
        token,
      );
      const haveType = new Set(
        existing.map((r) => r.type).filter((t): t is string => Boolean(t)),
      );
      for (const model of LOCATION_SEED_MODELS) {
        if (haveType.has(model.type)) continue;
        await apiPost('/inventory/locations', { name: model.name, type: model.type }, token);
        haveType.add(model.type);
      }
      await refreshReadiness();
      await loadSetupLocations();
    } catch (e: unknown) {
      setLocationSeedErr(getDisplayError(e));
    } finally {
      setLocationSeedBusy(false);
    }
  }, [refreshReadiness, loadSetupLocations]);

  const refresh = useCallback(() => {
    void refreshReadiness();
  }, [refreshReadiness]);

  const openWizard = useCallback(
    (opts?: { step?: number }) => {
      setSetupDismissed(user?.id, false);
      setReopenAfterNav(false);
      if (opts?.step != null) setStepIndex(opts.step);
      setOpen(true);
      setExpanded(true);
      void refreshReadiness();
    },
    [refreshReadiness, user?.id],
  );

  const collapsePanel = useCallback(() => {
    setExpanded(false);
  }, []);

  const expandPanel = useCallback(() => {
    setExpanded(true);
  }, []);

  /** Only explicit exit before foundations complete. */
  const dismissForLater = useCallback(() => {
    setDismissConfirmOpen(false);
    setSetupDismissed(user?.id, true);
    setReopenAfterNav(false);
    setOpen(false);
    setExpanded(false);
  }, [user?.id]);

  const requestDismissForLater = useCallback(() => {
    setDismissConfirmOpen(true);
  }, []);

  useEffect(() => {
    setSetupWizardUiState({
      visible: open,
      expanded: open && expanded,
      collapseInstant: false,
    });
  }, [open, expanded]);

  useEffect(() => {
    return () => setSetupWizardUiState({ visible: false, expanded: false, collapseInstant: false });
  }, []);

  useEffect(() => {
    return subscribeSetupWizardUiState(() => {
      const s = getSetupWizardUiState();
      if (!s.visible) return;
      if (!s.expanded) {
        if (s.collapseInstant) setCollapseInstant(true);
        setExpanded(false);
      }
    });
  }, []);

  useEffect(() => {
    if (!collapseInstant) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setCollapseInstant(false));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [collapseInstant, expanded]);

  useEffect(() => {
    return subscribeOpenSetup((opts) => openWizard(opts));
  }, [openWizard]);

  useEffect(() => {
    if (!open) {
      document.documentElement.style.removeProperty(SETUP_HEIGHT_VAR);
      document.documentElement.style.removeProperty(SETUP_PAD_VAR);
      return;
    }
    const el = sheetRef.current;
    if (!el) return;
    const publish = () => publishSetupMetrics(el);
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      document.documentElement.style.removeProperty(SETUP_HEIGHT_VAR);
      document.documentElement.style.removeProperty(SETUP_PAD_VAR);
    };
  }, [open, expanded]);

  useEffect(() => {
    if (!user || mustChangePassword || autoTried) return;
    if (!user.roles?.includes('SUPER_ADMIN')) {
      setAutoTried(true);
      return;
    }
    if (isSetupDismissed(user.id)) {
      setAutoTried(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      const payload = await refreshReadiness();
      if (cancelled) return;
      setAutoTried(true);
      if (!payload) return;
      if (payload.foundationsComplete) return;
      if (isSetupDismissed(user.id)) return;
      setOpen(true);
      setExpanded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, mustChangePassword, autoTried, refreshReadiness]);

  /* Deep-link pause: reopen after route settles unless completed or « Plus tard ». */
  useEffect(() => {
    if (!reopenAfterNav) return;
    if (!user?.id || isSetupDismissed(user.id) || complete) {
      setReopenAfterNav(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setOpen(true);
      setExpanded(true);
      void refreshReadiness();
      setReopenAfterNav(false);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [location.pathname, reopenAfterNav, user?.id, complete, refreshReadiness]);

  useEffect(() => {
    return subscribeCustomRoleCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingRoleCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingRoleCreate(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('roles'));
        void refreshReadiness();
        void loadPresentRoles();
        return;
      }
      /* cancelled */
      setAwaitingRoleCreate(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('roles'));
    });
  }, [refreshReadiness, loadPresentRoles]);

  useEffect(() => {
    return subscribeRolesBulkImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingRoleImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingRoleImport(false);
        if (ev.createdCount > 0) setRolesImportDone(true);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('roles'));
        void refreshReadiness();
        void loadPresentRoles();
        return;
      }
      setAwaitingRoleImport(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('roles'));
    });
  }, [refreshReadiness, loadPresentRoles]);

  useEffect(() => {
    return subscribeStaffUserCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingStaffUser(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingStaffUser(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('team'));
        void refreshReadiness();
        return;
      }
      setAwaitingStaffUser(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('team'));
    });
  }, [refreshReadiness]);

  useEffect(() => {
    return subscribeStaffUsersBulkImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingStaffUsersImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingStaffUsersImport(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('team'));
        void refreshReadiness();
        return;
      }
      setAwaitingStaffUsersImport(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('team'));
    });
  }, [refreshReadiness]);

  useEffect(() => {
    return subscribeMarkeCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingMarkeCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingMarkeCreate(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('markes'));
        void refreshReadiness();
        void loadSetupMarkes();
        return;
      }
      setAwaitingMarkeCreate(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('markes'));
    });
  }, [refreshReadiness, loadSetupMarkes]);

  useEffect(() => {
    return subscribeMarkeImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingMarkeImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingMarkeImport(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('markes'));
        void refreshReadiness();
        void loadSetupMarkes();
        return;
      }
      setAwaitingMarkeImport(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('markes'));
    });
  }, [refreshReadiness, loadSetupMarkes]);

  useEffect(() => {
    return subscribeTagFamilyCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingTagFamilyCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingTagFamilyCreate(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('tags'));
        void refreshReadiness();
        void loadSetupTagFamilies();
        return;
      }
      setAwaitingTagFamilyCreate(false);
    });
  }, [refreshReadiness, loadSetupTagFamilies]);

  useEffect(() => {
    return subscribeTagsBulkImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingTagsImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingTagsImport(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('tags'));
        void refreshReadiness();
        void loadSetupTagFamilies();
        return;
      }
      setAwaitingTagsImport(false);
    });
  }, [refreshReadiness, loadSetupTagFamilies]);


  useEffect(() => {
    return subscribeProductTypeCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingProductTypeCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingProductTypeCreate(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('structure'));
        void refreshReadiness();
        void loadSetupProductTypes();
        return;
      }
      setAwaitingProductTypeCreate(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('structure'));
    });
  }, [refreshReadiness, loadSetupProductTypes]);

  const accompanyPersonalizedRoleCreate = useCallback(() => {
    beginAwaitCustomRoleCreate();
    setExpanded(false);
    navigate('/roles?create=1');
  }, [navigate]);

  const accompanyRolesBulkImport = useCallback(() => {
    beginAwaitRolesBulkImport();
    setExpanded(false);
    navigate('/roles?import=1');
  }, [navigate]);

  const accompanyStaffUserCreate = useCallback(
    (roleName: string) => {
      beginAwaitStaffUserCreate(roleName);
      setExpanded(false);
      navigate(
        `${ADMIN_USERS.path}?create=1&role=${encodeURIComponent(roleName)}`,
      );
    },
    [navigate],
  );

  const accompanyStaffUsersImport = useCallback(() => {
    beginAwaitStaffUsersImport();
    setExpanded(false);
    navigate(`${ADMIN_USERS.path}?import=1`);
  }, [navigate]);

  const tagSeedModels = useMemo(() => {
    const byCode = new Map(setupTagFamilies.map((g) => [g.code, g]));
    return CANONICAL_PRODUCT_TAG_GROUPS.map((m) => {
      const existing = byCode.get(m.code);
      return {
        code: m.code,
        name: m.name,
        present: Boolean(existing && existing.tagCount > 0),
        valueLabels: m.tags.map((tg) => tg.label),
      };
    });
  }, [setupTagFamilies]);

  const tagSeedComplete = tagSeedModels.every((m) => m.present);

  const installTagSeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setTagSeedErr('Session requise.');
      return;
    }
    setTagSeedBusy(true);
    setTagSeedErr(null);
    try {
      await apiPost('/catalog/admin/tags/ensure-seed', {}, token);
      await refreshReadiness();
      await loadSetupTagFamilies();
    } catch (e: unknown) {
      setTagSeedErr(getDisplayError(e));
    } finally {
      setTagSeedBusy(false);
    }
  }, [refreshReadiness, loadSetupTagFamilies]);

  /** Boot normally seeds tags; re-ensure when opening this step if incomplete. */
  useEffect(() => {
    if (!open || !expanded || STEPS[stepIndex]?.id !== 'tags') return;
    if (tagSeedComplete || tagSeedBusy) return;
    void installTagSeedModels();
  }, [open, expanded, stepIndex, tagSeedComplete, tagSeedBusy, installTagSeedModels]);

  const accompanyTagFamilyCreate = useCallback(() => {
    beginAwaitTagFamilyCreate();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=tags&create=1');
  }, [navigate]);

  const accompanyTagsImport = useCallback(() => {
    beginAwaitTagsBulkImport();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=tags&import=1');
  }, [navigate]);

  const accompanyMarkeCreate = useCallback(() => {
    beginAwaitMarkeCreate();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=marques&create=1');
  }, [navigate]);

  const accompanyMarkeImport = useCallback(() => {
    beginAwaitMarkeImport();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=marques&import=1');
  }, [navigate]);

  const accompanyProductTypeCreate = useCallback(() => {
    beginAwaitProductTypeCreate();
    setExpanded(false);
    navigate('/catalog/types?createType=1');
  }, [navigate]);

  const accompanyCategoryCreate = useCallback(() => {
    beginAwaitStructureCreate();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=structure&createStructure=1');
  }, [navigate]);

  useEffect(() => {
    return subscribeStructureCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingStructureCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingStructureCreate(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('structure'));
        void refreshReadiness();
        void loadSetupProductTypes();
        void loadSetupCategories();
        void loadTaxonomySeedModels();
        return;
      }
      setAwaitingStructureCreate(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('structure'));
    });
  }, [refreshReadiness, loadSetupProductTypes, loadSetupCategories, loadTaxonomySeedModels]);

  const accompanyLocationCreate = useCallback(() => {
    beginAwaitLocationCreate();
    setExpanded(false);
    navigate('/inventory/locations?create=1');
  }, [navigate]);

  useEffect(() => {
    return subscribeLocationCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingLocationCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingLocationCreate(false);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('locations'));
        void refreshReadiness();
        void loadSetupLocations();
        return;
      }
      setAwaitingLocationCreate(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('locations'));
    });
  }, [refreshReadiness, loadSetupLocations]);

  type TeamMemberFace = { id: string; label: string; src: string | null };
  const [teamFacesByRole, setTeamFacesByRole] = useState<Record<string, TeamMemberFace[]>>(
    {},
  );
  const [facePreview, setFacePreview] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    if (!open || !expanded) return;
    if (STEPS[stepIndex]?.id !== 'team') return;
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    void apiGetListSimple<UserRow>('/admin/users', token)
      .then((rows) => {
        if (cancelled) return;
        const next: Record<string, TeamMemberFace[]> = {};
        for (const u of rows) {
          if (!u.active || u.deletedAt) continue;
          const label = formatUserFullName(u)?.trim() || u.email;
          const face: TeamMemberFace = {
            id: u.id,
            label,
            src: u.profilePictureUrl ?? null,
          };
          for (const role of u.roles ?? []) {
            const list = next[role] ?? [];
            list.push(face);
            next[role] = list;
          }
        }
        setTeamFacesByRole(next);
      })
      .catch(() => {
        if (!cancelled) setTeamFacesByRole({});
      });
    return () => {
      cancelled = true;
    };
  }, [open, expanded, stepIndex, foundations.team.activeStaffCount, foundations.team.slotsMet]);

  /**
   * Esc → collapse when incomplete; dismiss when done.
   * Backdrop same: collapse incomplete, dismiss complete.
   * While awaiting role create, Esc only collapses (already collapsed).
   */
  useEffect(() => {
    if (!open || !expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      if (!complete) {
        collapsePanel();
        return;
      }
      requestDismissForLater();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, expanded, complete, collapsePanel, requestDismissForLater]);

  const currentStep = STEPS[stepIndex];
  /** Soft preference steps stay after hard complete — do not jump to Terminé via `complete`. */
  const onDonePanel = stepIndex >= STEPS.length;

  const stepDone = useMemo(() => {
    return STEPS.map((s) => {
      if (s.id === 'roles' && (customRoles.length > 0 || roleSuggestComplete)) return true;
      return isFoundationStepComplete(s.id, foundations);
    });
  }, [foundations, customRoles.length, roleSuggestComplete]);

  /** Unmet roles first so empty slots stay in the 3-row window. */
  const teamSlotsOrdered = useMemo(() => {
    const slots = foundations.team.slots;
    return [...slots].sort((a, b) => Number(a.met) - Number(b.met));
  }, [foundations.team.slots]);

  const canContinueStep = onDonePanel ? true : stepDone[stepIndex] === true;

  const headerStepLabel = onDonePanel
    ? 'Terminé'
    : (currentStep?.label ?? STEPS[0]!.label);

  const progressLabel = `${pct}\u00a0%`;

  const headerAria = onDonePanel
    ? `Configuration initiale, ${progressLabel}, terminé`
    : `Configuration initiale, ${progressLabel}, étape ${headerStepLabel}`;

  const finishAndHandoff = () => {
    setSetupDismissed(user?.id, true);
    setReopenAfterNav(false);
    setOpen(false);
    setExpanded(false);
    openLaunchWizard();
  };

  if (!open || typeof document === 'undefined') return null;

  const sheet = (
    <div className={dockStyles.portalHost} data-wp-setup-wizard="">
      <div
        className={
          expanded ? `${dockStyles.backdrop} ${dockStyles.backdropVisible}` : dockStyles.backdrop
        }
        aria-hidden
        onMouseDown={(e) => {
          if (!expanded || e.target !== e.currentTarget) return;
          if (!complete) {
            collapsePanel();
            return;
          }
          requestDismissForLater();
        }}
      />
      <aside
        ref={sheetRef}
        className={
          expanded
            ? `${dockStyles.sheet} ${dockStyles.sheetExpanded}`
            : `${dockStyles.sheet} ${dockStyles.sheetCollapsed}`
        }
        data-wp-collapse-instant={collapseInstant ? '' : undefined}
        role="dialog"
        aria-modal={expanded ? true : undefined}
        aria-labelledby={titleId}
        aria-expanded={expanded}
        {...overlayPresenceProps(expanded)}
      >
        <div className={styles.stripChrome}>
          <div className={`${dockStyles.strip} ${styles.stripRow}`} data-wp-dock-strip="">
            <button
              type="button"
              className={styles.stripToggle}
              onClick={() => (expanded ? collapsePanel() : expandPanel())}
              aria-label={
                expanded ? `${headerAria}. Réduire.` : `${headerAria}. Ouvrir.`
              }
            >
              <span className={styles.headerLead}>
                <span id={titleId} className={dockStyles.stripCopy}>
                  Configuration initiale
                </span>
                {!expanded ? (
                  <span className={styles.headerStepHint}>
                    {awaitingRoleImport
                      ? 'Import de rôles…'
                      : awaitingRoleCreate
                        ? 'Création d’un rôle…'
                        : awaitingStaffUsersImport
                          ? 'Import de comptes…'
                          : awaitingStaffUser
                            ? 'Création d’un compte…'
                            : awaitingMarkeImport
                              ? 'Import de marques…'
                              : awaitingMarkeCreate
                                ? 'Création d’une marque…'
                                : awaitingProductTypeCreate
                                  ? 'Création d’un type…'
                                  : awaitingStructureCreate
                                    ? 'Création d’une structure…'
                                  : awaitingLocationCreate
                                    ? 'Création d’un emplacement…'
                                    : awaitingTagsImport
                                      ? 'Import de tags…'
                                      : awaitingTagFamilyCreate
                                        ? 'Création d’une famille de tags…'
                                        : headerStepLabel}
                  </span>
                ) : null}
              </span>
              <span className={dockStyles.stripEnd}>
                <span className={styles.progressPct}>{progressLabel}</span>
                <span
                  className={
                    expanded
                      ? `${dockStyles.pillTrack} ${dockStyles.pillTrackWide}`
                      : dockStyles.pillTrack
                  }
                  aria-hidden
                >
                  <span className={dockStyles.pillFill} style={{ width: `${pct}%` }} />
                </span>
                <IconChevronRight
                  size={18}
                  stroke={1.6}
                  aria-hidden
                  className={
                    expanded
                      ? `${dockStyles.stripChevron} ${dockStyles.stripChevronExpanded}`
                      : dockStyles.stripChevron
                  }
                />
              </span>
            </button>
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              borderless
              className={styles.stripDismiss}
              aria-label="Plus tard"
              title="Plus tard"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                requestDismissForLater();
              }}
            >
              <IconClock size={18} stroke={1.6} aria-hidden />
            </IconButton>
          </div>
          {expanded ? (
            <ProximityScope axis="x" className={styles.stepTabs} aria-label="Étapes">
              {STEPS.map((s, i) => {
                const done = stepDone[i];
                const active = !onDonePanel && i === stepIndex;
                const canGo = onDonePanel || i < stepIndex;
                const className = [
                  styles.stepTab,
                  active ? styles.stepTabActive : '',
                  done ? styles.stepTabDone : '',
                  canGo ? styles.stepTabNav : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const label = `${i + 1}. ${s.label}`;
                if (canGo) {
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-wp-proximity-item=""
                      className={className}
                      aria-current={active ? 'step' : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setStepIndex(i);
                      }}
                    >
                      {label}
                    </button>
                  );
                }
                return (
                  <span
                    key={s.id}
                    data-wp-proximity-item=""
                    className={className}
                    aria-current={active ? 'step' : undefined}
                  >
                    {label}
                  </span>
                );
              })}
            </ProximityScope>
          ) : null}
        </div>

        <div className={dockStyles.expandSlot} aria-hidden={!expanded}>
          <div className={dockStyles.expandInner} {...(!expanded ? { inert: true } : {})}>
            <div
              id="setup-wizard-drawer"
              className={[
                dockStyles.body,
                !onDonePanel &&
                (currentStep?.id === 'roles' ||
                  currentStep?.id === 'team' ||
                  currentStep?.id === 'markes' ||
                  currentStep?.id === 'structure' ||
                  currentStep?.id === 'locations')
                  ? styles.bodyFill
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              data-tour="setup-wizard-drawer"
            >
              <StepTravel
                index={onDonePanel ? STEPS.length : stepIndex}
                fill={
                  !onDonePanel &&
                  (currentStep?.id === 'roles' ||
                    currentStep?.id === 'team' ||
                    currentStep?.id === 'markes' ||
                    currentStep?.id === 'structure' ||
                    currentStep?.id === 'locations')
                }
              >
              {onDonePanel ? (
                <section className={styles.stepBlock}>
                  <h2 className={styles.stepTitle}>Prêt</h2>
                  <Text size="sm" c="dimmed">
                    Enchaînez sur le contrôle de lancement.
                  </Text>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'roles' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={styles.rolePaths}>
                    <div className={styles.rolePath}>
                      <div
                        className={`${styles.rolePathScroll} wp-scroll-fade`}
                        data-wp-mist-scroll=""
                      >
                        <h3 className={styles.rolePathTitle}>Assurés</h3>
                        <div
                          className={`${styles.roleTagGrid} ${styles.roleTagGridFlow}`}
                          role="list"
                          aria-label="Rôles assurés par la plateforme"
                        >
                          {SETUP_ENSURED_ROLE_NAMES.map((name) => {
                            const label = userRoleLabelFr(name);
                            return (
                              <span
                                key={name}
                                role="listitem"
                                className={`${styles.roleTag} ${styles.roleTagPresent}`}
                              >
                                <RoleTagFace roleName={name} />
                                <span className={styles.roleTagLabel}>{label}</span>
                                <span
                                  className={styles.roleTagCheck}
                                  aria-label={`${label} — assuré`}
                                  title="Assuré"
                                >
                                  <IconCheck size={14} stroke={2.2} aria-hidden />
                                </span>
                                <Tooltip
                                  wide
                                  portal
                                  position="bottom"
                                  label={<RoleCapabilityTip roleName={name} />}
                                >
                                  <button
                                    type="button"
                                    className={styles.roleTagInfo}
                                    tabIndex={expanded ? undefined : -1}
                                    aria-label={`Capacités : ${label}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                                  </button>
                                </Tooltip>
                              </span>
                            );
                          })}
                        </div>

                        <h3 className={styles.rolePathTitle}>Modèles suggérés</h3>
                        <div
                          className={`${styles.roleTagGrid} ${styles.roleTagGridFlow}`}
                          role="list"
                          aria-label="Modèles de rôles suggérés"
                        >
                          {SETUP_ROLE_SUGGESTION_NAMES.map((name) => {
                            const label = userRoleLabelFr(name);
                            const present = suggestedRolesPresent.has(name);
                            return (
                              <span
                                key={name}
                                role="listitem"
                                className={[
                                  styles.roleTag,
                                  present ? styles.roleTagPresent : styles.roleTagPending,
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                <RoleTagFace roleName={name} />
                                <span className={styles.roleTagLabel}>{label}</span>
                                {present ? (
                                  <span
                                    className={styles.roleTagCheck}
                                    aria-label={`${label} — installé`}
                                    title="Installé"
                                  >
                                    <IconCheck size={14} stroke={2.2} aria-hidden />
                                  </span>
                                ) : null}
                                <Tooltip
                                  wide
                                  portal
                                  position="bottom"
                                  label={<RoleCapabilityTip roleName={name} />}
                                >
                                  <button
                                    type="button"
                                    className={styles.roleTagInfo}
                                    tabIndex={expanded ? undefined : -1}
                                    aria-label={`Capacités : ${label}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                                  </button>
                                </Tooltip>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      {roleSuggestErr ? <InlineError>{roleSuggestErr}</InlineError> : null}
                      {!roleSuggestComplete ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={roleSuggestBusy}
                            onClick={() => void installSuggestedRoleModels()}
                          >
                            {roleSuggestBusy ? 'Installation…' : 'Installer les modèles'}
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Nouveau rôle</h3>
                      {customRoles.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Rôles personnalisés"
                        >
                          {customRoles.map((role) => {
                            const label = userRoleLabelFr(role.name);
                            return (
                              <span
                                key={role.id}
                                role="listitem"
                                className={`${styles.roleTag} ${styles.roleTagPresent}`}
                              >
                                <RoleTagFace roleName={role.name} />
                                <span className={styles.roleTagLabel}>{label}</span>
                                <span
                                  className={styles.roleTagCheck}
                                  aria-label={`${label} — créé`}
                                  title="Créé"
                                >
                                  <IconCheck size={14} stroke={2.2} aria-hidden />
                                </span>
                                <Tooltip
                                  wide
                                  portal
                                  position="bottom"
                                label={
                                  <RoleCapabilityTip roleName={role.name} packs={role.packs} />
                                }
                              >
                                <button
                                  type="button"
                                  className={styles.roleTagInfo}
                                  tabIndex={expanded ? undefined : -1}
                                  aria-label={`Capacités : ${label}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                                </button>
                              </Tooltip>
                            </span>
                          );
                        })}
                      </RoleTagGridShell>
                      ) : (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                        >
                          <SetupWizardPathArt kind="create-role" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="soft"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyPersonalizedRoleCreate}
                        >
                          Créer un rôle
                        </Button>
                      </div>
                    </div>

                    <div
                      className={[
                        styles.rolePath,
                        styles.rolePathCustom,
                        rolesImportDone ? styles.rolePathDisabled : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-disabled={rolesImportDone || undefined}
                    >
                      <h3 className={styles.rolePathTitle}>Importer</h3>
                      <div
                        className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                        aria-hidden
                        title={
                          rolesImportDone
                            ? 'Import effectué'
                            : 'Import en masse depuis un fichier'
                        }
                      >
                        {rolesImportDone ? (
                          <span className={`${styles.roleImportGlyph} ${styles.roleImportGlyphDone}`}>
                            <IconCheck size={28} stroke={2} aria-hidden />
                          </span>
                        ) : (
                          <SetupWizardPathArt kind="import" />
                        )}
                      </div>
                      {!rolesImportDone ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            tabIndex={expanded ? undefined : -1}
                            onClick={() => {
                              const token = getToken();
                              if (token) void downloadRolesImportTemplate(token, 'xlsx');
                            }}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconFileTypeXls size={16} stroke={1.5} aria-hidden />
                              Modèle
                            </span>
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            tabIndex={expanded ? undefined : -1}
                            onClick={() => {
                              const token = getToken();
                              if (token) void downloadRolesImportTemplate(token, 'csv');
                            }}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconFileTypeCsv size={16} stroke={1.5} aria-hidden />
                              CSV
                            </span>
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="soft"
                            tabIndex={expanded ? undefined : -1}
                            onClick={accompanyRolesBulkImport}
                          >
                            Importer un fichier
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'team' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={styles.teamPaths}>
                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer</h3>
                      {teamSlotsOrdered.length === 0 ? (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                        >
                          <SetupWizardPathArt kind="team" />
                        </div>
                      ) : (
                      <TeamRoleListShell
                        className={styles.teamRoleListScroll}
                        aria-label="Comptes par rôle"
                      >
                        <div className={styles.teamRoleList}>
                          {teamSlotsOrdered.map((slot) => {
                            const faces = teamFacesByRole[slot.id] ?? [];
                            const label = userRoleLabelFr(slot.id);
                            const needsStaff = !slot.met;
                            return (
                              <div
                                key={slot.id}
                                className={[
                                  styles.teamRoleRow,
                                  needsStaff ? styles.teamRoleRowUnmet : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                                aria-invalid={needsStaff || undefined}
                              >
                                <div className={styles.teamRoleMain}>
                                  <UserRolePill role={slot.id} />
                                  <div
                                    className={styles.teamFaceStack}
                                    aria-label={`Comptes — ${label}`}
                                  >
                                    {faces.slice(0, 4).map((face) => (
                                      <span
                                        key={face.id}
                                        className={styles.teamMemberChip}
                                        title={face.label}
                                      >
                                        {face.src ? (
                                          <button
                                            type="button"
                                            className={styles.teamFaceZoom}
                                            tabIndex={expanded ? undefined : -1}
                                            aria-label={`Agrandir la photo de ${face.label}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setFacePreview({ src: face.src!, alt: face.label });
                                            }}
                                          >
                                            <Avatar
                                              size="sm"
                                              label={face.label}
                                              src={face.src}
                                              className={styles.teamFace}
                                            />
                                          </button>
                                        ) : (
                                          <Avatar
                                            size="sm"
                                            label={face.label}
                                            src={face.src}
                                            className={styles.teamFace}
                                          />
                                        )}
                                        <span className={styles.teamMemberName}>{face.label}</span>
                                      </span>
                                    ))}
                                    {faces.length > 4 ? (
                                      <span className={styles.teamFaceMore}>
                                        +{faces.length - 4}
                                      </span>
                                    ) : null}
                                    {slot.met ? (
                                      <span
                                        className={styles.teamRoleCheck}
                                        aria-label={`${label} — pourvu`}
                                        title="Pourvu"
                                      >
                                        <IconCheck size={14} stroke={2.2} aria-hidden />
                                      </span>
                                    ) : (
                                      <span className={styles.teamRoleUnmetHint} aria-live="polite">
                                        Aucun compte
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <IconButton
                                  type="button"
                                  variant="soft"
                                  size="sm"
                                  aria-label={`Créer un compte pour ${label}`}
                                  tabIndex={expanded ? undefined : -1}
                                  onClick={() => accompanyStaffUserCreate(slot.id)}
                                >
                                  <IconPlus {...WP_ICON_NAV} />
                                </IconButton>
                              </div>
                            );
                          })}
                        </div>
                      </TeamRoleListShell>
                      )}
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Importer</h3>
                      <div
                        className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                        aria-hidden
                        title="Import en masse depuis un fichier"
                      >
                        <SetupWizardPathArt kind="import" />
                      </div>
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          tabIndex={expanded ? undefined : -1}
                          onClick={() => {
                            const token = getToken();
                            if (token) void downloadUsersImportTemplate(token, 'xlsx');
                          }}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconFileTypeXls size={16} stroke={1.5} aria-hidden />
                            Modèle
                          </span>
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          tabIndex={expanded ? undefined : -1}
                          onClick={() => {
                            const token = getToken();
                            if (token) void downloadUsersImportTemplate(token, 'csv');
                          }}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconFileTypeCsv size={16} stroke={1.5} aria-hidden />
                            CSV
                          </span>
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="soft"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyStaffUsersImport}
                        >
                          Importer des comptes
                        </Button>
                      </div>
                    </div>
                  </div>
                  {foundations.team.slotsMet && !foundations.team.staffFloorMet ? (
                    <InlineError>
                      {foundations.team.activeStaffCount}/2 comptes — créez-en un autre.
                    </InlineError>
                  ) : null}
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'markes' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={styles.teamPaths}>
                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer</h3>
                      {setupMarkes.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Marques créées"
                        >
                          {setupMarkes.map((m) => (
                            <span
                              key={m.id}
                              role="listitem"
                              className={`${styles.roleTag} ${styles.roleTagPresent}`}
                            >
                              <MarkeLogoThumb url={m.logoUrl} name={m.name} size="sm" />
                              <span className={styles.roleTagLabel}>{m.name}</span>
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${m.name} — créée`}
                                title="Créée"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            </span>
                          ))}
                        </RoleTagGridShell>
                      ) : (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                          title="Créer une marque"
                        >
                          <SetupWizardPathArt kind="brand" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="soft"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyMarkeCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconPlus size={16} stroke={1.5} aria-hidden />
                            Nouvelle marque
                          </span>
                        </Button>
                      </div>
                    </div>
                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Importer</h3>
                      <div
                        className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                        aria-hidden
                        title="Import de marques"
                      >
                        <SetupWizardPathArt kind="import" />
                      </div>
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          tabIndex={expanded ? undefined : -1}
                          onClick={() => downloadFacetTemplate('markes', 'xlsx')}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconFileTypeXls size={16} stroke={1.5} aria-hidden />
                            Modèle
                          </span>
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          tabIndex={expanded ? undefined : -1}
                          onClick={() => downloadFacetTemplate('markes', 'csv')}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconFileTypeCsv size={16} stroke={1.5} aria-hidden />
                            CSV
                          </span>
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="soft"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyMarkeImport}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconPackageImport size={16} stroke={1.5} aria-hidden />
                            Importer un fichier
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'structure' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={`${styles.rolePaths} ${styles.structurePaths}`}>
                    <div className={`${styles.rolePath} ${styles.structureGraphPanel}`}>
                      <h3 className={styles.rolePathTitle}>Types & catégories</h3>
                      <div
                        className={cx(
                          styles.taxonomyGraphShell,
                          expanded && styles.taxonomyGraphShellExpanded,
                        )}
                      >
                        {(taxonomySeedModels?.preview.productTypes.length ?? 0) > 0 ? (
                          <ProductTypeTaxonomyGraph
                            compact
                            productTypes={taxonomySeedModels!.preview.productTypes}
                            categories={taxonomySeedModels!.preview.categories}
                            selectedTypeCode={seedPreviewTypeCode}
                            canEdit={false}
                            token={getToken() ?? ''}
                            onChange={loadTaxonomySeedModels}
                            onSelectType={setSeedPreviewTypeCode}
                          />
                        ) : (
                          <div
                            className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                            title="Modèles taxonomy"
                          >
                            <SetupWizardPathArt kind="structure" />
                          </div>
                        )}
                      </div>
                      {taxonomySeedErr ? <InlineError>{taxonomySeedErr}</InlineError> : null}
                      {!taxonomySeedModels?.complete ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={taxonomySeedBusy}
                            onClick={() => void installTaxonomySeedModels()}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconDownload size={16} stroke={1.5} aria-hidden />
                              {taxonomySeedBusy ? 'Installation…' : 'Installer types & catégories'}
                            </span>
                          </Button>
                        </div>
                      ) : (
                        <p className={styles.taxonomySeedDoneHint}>Types & catégories installés</p>
                      )}
                    </div>

                    <div className={`${styles.rolePath} ${styles.structureGraphPanel}`}>
                      <h3 className={styles.rolePathTitle}>Structure</h3>
                      <div
                        className={cx(
                          styles.taxonomyGraphShell,
                          expanded && styles.taxonomyGraphShellExpanded,
                        )}
                      >
                        {hasSetupTaxonomyTree ? (
                          <ProductTypeTaxonomyGraph
                            compact
                            productTypes={setupSortedProductTypes}
                            categories={setupCategories}
                            selectedTypeCode={setupSelectedTypeCode}
                            canEdit={false}
                            token={getToken() ?? ''}
                            onChange={reloadSetupTaxonomy}
                            onSelectType={setSetupSelectedTypeCode}
                          />
                        ) : (
                          <div
                            className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter} ${styles.structureDropHint}`}
                            title="Créer une structure"
                          >
                            <SetupWizardPathArt kind="structure" />
                            <p className={styles.structureDropHintText}>
                              Aucune structure installée. Utilisez « Installer types & catégories » ou
                              créez-en une.
                            </p>
                          </div>
                        )}
                      </div>
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyCategoryCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconPlus size={16} stroke={1.5} aria-hidden />
                            Nouvelle structure
                          </span>
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="soft"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyProductTypeCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconPlus size={16} stroke={1.5} aria-hidden />
                            Nouveau type
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'locations' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={styles.teamPaths}>
                    <div className={styles.rolePath}>
                      <h3 className={styles.rolePathTitle}>Installer les modèles</h3>
                      <RoleTagGridShell
                        className={styles.roleTagGrid}
                        aria-label="Emplacements modèles"
                      >
                        {locationSeedModels.map((m) => (
                          <span
                            key={m.type}
                            role="listitem"
                            className={[
                              styles.roleTag,
                              m.present ? styles.roleTagPresent : styles.roleTagPending,
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            title={m.name}
                          >
                            <span className={styles.roleTagLabel}>{m.label}</span>
                            {m.present ? (
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${m.label} — installé`}
                                title="Installé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            ) : null}
                          </span>
                        ))}
                      </RoleTagGridShell>
                      {locationSeedErr ? <InlineError>{locationSeedErr}</InlineError> : null}
                      {!locationSeedComplete ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={locationSeedBusy}
                            onClick={() => void installLocationSeedModels()}
                          >
                            {locationSeedBusy ? 'Installation…' : 'Installer les modèles'}
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer</h3>
                      {setupLocations.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Emplacements créés"
                        >
                          {setupLocations.map((loc) => (
                            <span
                              key={loc.id}
                              role="listitem"
                              className={`${styles.roleTag} ${styles.roleTagPresent}`}
                              title={loc.type ?? undefined}
                            >
                              <span className={styles.roleTagLabel}>{loc.name}</span>
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${loc.name} — créé`}
                                title="Créé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            </span>
                          ))}
                        </RoleTagGridShell>
                      ) : (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                          title="Créer un emplacement"
                        >
                          <SetupWizardPathArt kind="location" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="soft"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyLocationCreate}
                        >
                          Nouvel emplacement
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              
              {!onDonePanel && currentStep?.id === 'tags' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <p className={styles.stepLede}>
                    Familles et valeurs assurées au démarrage (routine, peau, préoccupations,
                    textures, vitrine). Créez des familles custom ensuite si besoin.
                  </p>
                  <div className={styles.rolePaths}>
                    <div className={styles.rolePath}>
                      <h3 className={styles.rolePathTitle}>Assurés</h3>
                      <RoleTagGridShell
                        className={styles.roleTagGrid}
                        aria-label="Familles de tags assurées"
                      >
                        {tagSeedModels.map((m) => (
                          <span
                            key={m.code}
                            role="listitem"
                            className={[
                              styles.roleTag,
                              m.present ? styles.roleTagPresent : styles.roleTagPending,
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            <span className={styles.roleTagLabel}>{m.name}</span>
                            {m.present ? (
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${m.name} — assuré`}
                                title="Assuré"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            ) : null}
                          </span>
                        ))}
                      </RoleTagGridShell>
                      {tagSeedErr ? <InlineError>{tagSeedErr}</InlineError> : null}
                      {!tagSeedComplete ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={tagSeedBusy}
                            onClick={() => void installTagSeedModels()}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconDownload size={16} stroke={1.5} aria-hidden />
                              {tagSeedBusy ? 'Installation…' : 'Réparer les tags'}
                            </span>
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer une famille</h3>
                      {setupTagFamilies.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Familles de tags créées"
                        >
                          {setupTagFamilies.map((g) => (
                            <span
                              key={g.id}
                              role="listitem"
                              className={`${styles.roleTag} ${styles.roleTagPresent}`}
                            >
                              <span className={styles.roleTagLabel}>
                                {g.name}
                                {g.tagCount > 0 ? ` (${g.tagCount})` : ''}
                              </span>
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${g.name} — créé`}
                                title="Créé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            </span>
                          ))}
                        </RoleTagGridShell>
                      ) : (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                          title="Créer une famille de tags"
                        >
                          <SetupWizardPathArt kind="options" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyTagFamilyCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconTags size={16} stroke={1.5} aria-hidden />
                            Nouvelle famille
                          </span>
                        </Button>
                      </div>
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Importer</h3>
                      <div
                        className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                        aria-hidden
                        title="Import en masse depuis un fichier"
                      >
                        <SetupWizardPathArt kind="import" />
                      </div>
                      <div className={styles.rolePathActions}>
                        <div className={styles.rolePathActionRow}>
                          <Button
                            type="button"
                            size="sm"
                            variant="soft"
                            tabIndex={expanded ? undefined : -1}
                            onClick={accompanyTagsImport}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconPackageImport size={16} stroke={1.5} aria-hidden />
                              Importer des tags
                            </span>
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            tabIndex={expanded ? undefined : -1}
                            onClick={() => downloadFacetTemplate('tags', 'xlsx')}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconFileTypeXls size={16} stroke={1.5} aria-hidden />
                              XLSX
                            </span>
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            tabIndex={expanded ? undefined : -1}
                            onClick={() => downloadFacetTemplate('tags', 'csv')}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconFileTypeCsv size={16} stroke={1.5} aria-hidden />
                              CSV
                            </span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

{!onDonePanel && currentStep?.id === 'pref_low_stock' ? (
                <section className={styles.stepBlock}>
                  <StockAlertsSettingsSection
                    ref={stockBasRef}
                    facet="lowStock"
                    showTitle={false}
                    hideSaveButton
                    quietToast
                  />
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'pref_near_expiry' ? (
                <section className={styles.stepBlock}>
                  <StockAlertsSettingsSection
                    ref={peremptionRef}
                    facet="nearExpiry"
                    showTitle={false}
                    hideSaveButton
                    quietToast
                  />
                </section>
              ) : null}
              </StepTravel>
            </div>

            <div className={dockStyles.footer}>
              <div className={`${dockStyles.footerInner} ${styles.footerRow}`}>
              {onDonePanel || stepIndex > 0 ? (
                <Button
                  type="button"
                  variant="soft"
                  tabIndex={expanded ? undefined : -1}
                  onClick={() => {
                    if (onDonePanel) {
                      setStepIndex(STEPS.length - 1);
                      return;
                    }
                    setStepIndex((i) => Math.max(0, i - 1));
                  }}
                >
                  Retour
                </Button>
              ) : (
                <span />
              )}
              <div className={styles.footerEnd}>
                {onDonePanel ? (
                  <Button
                    type="button"
                    variant="primary"
                    tabIndex={expanded ? undefined : -1}
                    onClick={finishAndHandoff}
                  >
                    Prêt lancement
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    tabIndex={expanded ? undefined : -1}
                    disabled={!canContinueStep}
                    onClick={() => {
                      void (async () => {
                        const stepId = STEPS[stepIndex]?.id;
                        if (stepId === 'pref_low_stock') {
                          const ok = await stockBasRef.current?.save();
                          if (ok === false) return;
                        } else if (stepId === 'pref_near_expiry') {
                          const ok = await peremptionRef.current?.save();
                          if (ok === false) return;
                        }
                        void refresh();
                        setStepIndex((i) => i + 1);
                      })();
                    }}
                  >
                    Continuer
                  </Button>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );

  return (
    <>
      {createPortal(sheet, document.body)}
      <Modal
        opened={dismissConfirmOpen}
        onClose={() => setDismissConfirmOpen(false)}
        title="Plus tard ?"
        footer={
          <ConfirmModalFooter
            cancelLabel="Rester"
            confirmLabel="Plus tard"
            onCancel={() => setDismissConfirmOpen(false)}
            onConfirm={dismissForLater}
          />
        }
      >
        <Text size="sm">
          La configuration initiale restera accessible. Vous pourrez la reprendre quand vous voulez.
        </Text>
      </Modal>
      <Modal
        opened={facePreview !== null}
        onClose={() => setFacePreview(null)}
        title={facePreview?.alt ?? 'Photo'}
        size="md"
      >
        {facePreview ? (
          <img
            src={facePreview.src}
            alt={facePreview.alt}
            className={styles.facePreviewImage}
          />
        ) : null}
      </Modal>
    </>
  );
}
